import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  Button,
  Steps,
  Table,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import StudentMultiSelect from "../../common/StudentMultiSelect";

const { Step } = Steps;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const CreateInspectionModal = ({
  visible,
  onCancel,
  onSubmit,
  loading,
  classes,
  streams,
  terms,
  academicYears,
  subjects,
  students,
  choices,
  onClassChange,
  onFiltersChange,
  inspection = null, // Optional: if provided, we're in edit mode
}) => {
  const isEditMode = !!inspection;
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [generalScores, setGeneralScores] = useState([]);
  const [skillScores, setSkillScores] = useState([]);
  const [subjectPerformances, setSubjectPerformances] = useState([]);

  // Initialize scores when choices are loaded
  useEffect(() => {
    if (choices?.generalAreas?.length > 0) {
      setGeneralScores(
        choices.generalAreas.map((area) => ({
          area: area.value,
          label: area.label,
          score: 0,
          areasToMaintain: "",
          areasToImprove: "",
        })),
      );
    }
    if (choices?.skillAreas?.length > 0) {
      setSkillScores(
        choices.skillAreas.map((area) => ({
          area: area.value,
          label: area.label,
          score: 0,
        })),
      );
    }
  }, [choices]);

  // Reset form when modal opens/closes or populate with inspection data
  useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setFormData({});

      if (isEditMode && inspection) {
        // Pre-fill form with existing inspection data
        form.setFieldsValue({
          classID: inspection.classID,
          streamID: inspection.streamID,
          termID: inspection.termID,
          acadyrID: inspection.acadyrID,
        });

        // Pre-fill general scores
        if (inspection.generalScores?.length > 0) {
          setGeneralScores(
            inspection.generalScores.map((score) => ({
              area: score.area,
              label: score.areaLabel || score.area,
              score: score.score,
              areasToMaintain: score.areasToMaintain || "",
              areasToImprove: score.areasToImprove || "",
            })),
          );
        }

        // Pre-fill skill scores
        if (inspection.skillScores?.length > 0) {
          setSkillScores(
            inspection.skillScores.map((score) => ({
              area: score.area,
              label: score.areaLabel || score.area,
              score: score.score,
            })),
          );
        }

        // Pre-fill subject performances
        if (inspection.subjectPerformances?.length > 0) {
          setSubjectPerformances(
            inspection.subjectPerformances.map((perf, index) => ({
              key: Date.now() + index,
              subjectID: perf.subjectID,
              areasToMaintain: perf.areasToMaintain || "",
              areasToImprove: perf.areasToImprove || "",
              excellingStudents: perf.excellingStudents || [],
              studentsNeedingHelp: perf.studentsNeedingHelp || [],
            })),
          );
        } else {
          setSubjectPerformances([]);
        }
      } else {
        // Create mode - reset everything
        form.resetFields();
        if (choices?.generalAreas?.length > 0) {
          setGeneralScores(
            choices.generalAreas.map((area) => ({
              area: area.value,
              label: area.label,
              score: 0,
              areasToMaintain: "",
              areasToImprove: "",
            })),
          );
        }
        if (choices?.skillAreas?.length > 0) {
          setSkillScores(
            choices.skillAreas.map((area) => ({
              area: area.value,
              label: area.label,
              score: 0,
            })),
          );
        }
        setSubjectPerformances([]);
      }
    }
  }, [visible, form, choices, isEditMode, inspection]);

  const handleClassChange = (classId) => {
    form.setFieldValue("streamID", null);
    if (onClassChange) {
      const formData = {
        classID: classId,
        streamID: form.getFieldValue("streamID"),
        acadyrID: form.getFieldValue("acadyrID"),
        termID: form.getFieldValue("termID"),
      };
      onClassChange(classId, formData);
    }
  };

  const handleFilterChange = (field, value) => {
    const formData = {
      classID: form.getFieldValue("classID"),
      streamID: form.getFieldValue("streamID"),
      acadyrID: form.getFieldValue("acadyrID"),
      termID: form.getFieldValue("termID"),
      [field]: value,
    };
    if (onFiltersChange && formData.classID) {
      onFiltersChange(formData);
    }
  };

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        const values = await form.validateFields([
          "classID",
          "termID",
          "acadyrID",
        ]);
        setFormData((prev) => ({ ...prev, ...values }));

        // Fetch students when moving to subject performance step (step 3)
        if (onFiltersChange && values.classID) {
          const formData = {
            classID: values.classID,
            streamID: form.getFieldValue("streamID"),
            acadyrID: values.acadyrID,
            termID: values.termID,
          };
          onFiltersChange(formData);
        }
      }
      setCurrentStep((prev) => prev + 1);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleGeneralScoreChange = (index, field, value) => {
    setGeneralScores((prev) => {
      const newScores = [...prev];
      newScores[index] = { ...newScores[index], [field]: value };
      return newScores;
    });
  };

  const handleSkillScoreChange = (index, value) => {
    setSkillScores((prev) => {
      const newScores = [...prev];
      newScores[index] = { ...newScores[index], score: value };
      return newScores;
    });
  };

  const addSubjectPerformance = () => {
    setSubjectPerformances((prev) => [
      ...prev,
      {
        key: Date.now(),
        subjectID: null,
        areasToMaintain: "",
        areasToImprove: "",
        excellingStudents: [],
        studentsNeedingHelp: [],
      },
    ]);
  };

  const removeSubjectPerformance = (key) => {
    setSubjectPerformances((prev) => prev.filter((item) => item.key !== key));
  };

  const handleSubjectPerformanceChange = (key, field, value) => {
    setSubjectPerformances((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields([
        "classID",
        "termID",
        "acadyrID",
      ]);

      // Validate general scores
      const hasInvalidGeneralScore = generalScores.some(
        (score) => score.score < 0 || score.score > 10,
      );
      if (hasInvalidGeneralScore) {
        message.error("General area scores must be between 0 and 10");
        return;
      }

      // Validate skill scores
      const hasInvalidSkillScore = skillScores.some(
        (score) => score.score < 0 || score.score > 100,
      );
      if (hasInvalidSkillScore) {
        message.error("Skill scores must be between 0 and 100");
        return;
      }

      // Validate supervisor's comments
      const hasInvalidSubject = subjectPerformances.some(
        (perf) => !perf.subjectID,
      );
      if (subjectPerformances.length > 0 && hasInvalidSubject) {
        message.error("Please select a subject for all supervisor's comments");
        return;
      }

      const data = {
        classID: values.classID,
        streamID: form.getFieldValue("streamID") || null,
        termID: values.termID,
        acadyrID: values.acadyrID,
        generalScores: generalScores.map((score) => ({
          area: score.area,
          score: score.score,
          areasToMaintain: score.areasToMaintain,
          areasToImprove: score.areasToImprove,
        })),
        skillScores: skillScores.map((score) => ({
          area: score.area,
          score: score.score,
        })),
        subjectPerformances: subjectPerformances.map((perf) => ({
          subjectID: perf.subjectID,
          areasToMaintain: perf.areasToMaintain,
          areasToImprove: perf.areasToImprove,
          excellingStudents: perf.excellingStudents,
          studentsNeedingHelp: perf.studentsNeedingHelp,
        })),
      };

      const result = await onSubmit(data);
      if (result) {
        onCancel();
      }
    } catch (error) {
      console.error("Submit failed:", error);
    }
  };

  const steps = [
    {
      title: "Basic Info",
      content: (
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="classID"
                label="Class"
                rules={[{ required: true, message: "Please select a class" }]}
              >
                <Select
                  placeholder="Select class"
                  onChange={handleClassChange}
                  showSearch
                  optionFilterProp="children"
                >
                  {classes?.map((cls) => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="streamID" label="Stream (Optional)">
                <Select
                  placeholder="Select stream"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onChange={(value) => handleFilterChange("streamID", value)}
                >
                  {streams?.map((stream) => (
                    <Option key={stream.id} value={stream.id}>
                      {stream.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="termID"
                label="Term"
                rules={[{ required: true, message: "Please select a term" }]}
              >
                <Select
                  placeholder="Select term"
                  onChange={(value) => handleFilterChange("termID", value)}
                >
                  {terms?.map((term) => (
                    <Option key={term.id} value={term.id}>
                      {term.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="acadyrID"
                label="Academic Year"
                rules={[
                  { required: true, message: "Please select academic year" },
                ]}
              >
                <Select
                  placeholder="Select academic year"
                  onChange={(value) => handleFilterChange("acadyrID", value)}
                >
                  {academicYears?.map((year) => (
                    <Option key={year.id} value={year.id}>
                      {year.acadyr}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ),
    },
    {
      title: "General Areas",
      content: (
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          <Table
            dataSource={generalScores}
            rowKey="area"
            pagination={false}
            size="small"
            columns={[
              {
                title: "NO",
                width: 50,
                render: (_, __, index) => index + 1,
              },
              {
                title: "General Area",
                dataIndex: "label",
                width: 250,
              },
              {
                title: "Score (/10)",
                width: 100,
                render: (_, record, index) => (
                  <InputNumber
                    min={0}
                    max={10}
                    value={record.score}
                    onChange={(value) =>
                      handleGeneralScoreChange(index, "score", value)
                    }
                    style={{ width: "100%" }}
                  />
                ),
              },
              {
                title: "Areas to Maintain",
                render: (_, record, index) => (
                  <Input
                    value={record.areasToMaintain}
                    onChange={(e) =>
                      handleGeneralScoreChange(
                        index,
                        "areasToMaintain",
                        e.target.value,
                      )
                    }
                    placeholder="Areas to maintain..."
                  />
                ),
              },
              {
                title: "Areas to Improve",
                render: (_, record, index) => (
                  <Input
                    value={record.areasToImprove}
                    onChange={(e) =>
                      handleGeneralScoreChange(
                        index,
                        "areasToImprove",
                        e.target.value,
                      )
                    }
                    placeholder="Areas to improve..."
                  />
                ),
              },
            ]}
          />
          <Card size="small" style={{ marginTop: 16 }}>
            <Text strong>
              Sub-Total:{" "}
              {generalScores.reduce((sum, s) => sum + (s.score || 0), 0)}/100
            </Text>
          </Card>
        </div>
      ),
    },
    {
      title: "Skills",
      content: (
        <div>
          <Table
            dataSource={skillScores}
            rowKey="area"
            pagination={false}
            size="small"
            columns={[
              {
                title: "NO",
                width: 50,
                render: (_, __, index) => index + 1,
              },
              {
                title: "Skill Area",
                dataIndex: "label",
              },
              {
                title: "Score (/100)",
                width: 120,
                render: (_, record, index) => (
                  <InputNumber
                    min={0}
                    max={100}
                    value={record.score}
                    onChange={(value) => handleSkillScoreChange(index, value)}
                    style={{ width: "100%" }}
                  />
                ),
              },
            ]}
          />
          <Card size="small" style={{ marginTop: 16 }}>
            <Text strong>
              Sub-Total:{" "}
              {skillScores.reduce((sum, s) => sum + (s.score || 0), 0)}/300
            </Text>
          </Card>
        </div>
      ),
    },
    {
      title: "Supervisor's Comments",
      content: (
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          <Button
            type="dashed"
            onClick={addSubjectPerformance}
            icon={<PlusOutlined />}
            style={{ marginBottom: 16, width: "100%" }}
          >
            Add Supervisor's Comment
          </Button>

          {subjectPerformances.map((perf, index) => (
            <Card
              key={perf.key}
              size="small"
              style={{ marginBottom: 16 }}
              title={`Subject ${index + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeSubjectPerformance(perf.key)}
                />
              }
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Subject" style={{ marginBottom: 8 }}>
                    <Select
                      placeholder="Select subject"
                      value={perf.subjectID}
                      onChange={(value) =>
                        handleSubjectPerformanceChange(
                          perf.key,
                          "subjectID",
                          value,
                        )
                      }
                      showSearch
                      optionFilterProp="children"
                    >
                      {subjects?.map((subject) => (
                        <Option key={subject.id} value={subject.id}>
                          {subject.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Areas to Maintain"
                    style={{ marginBottom: 8 }}
                  >
                    <TextArea
                      rows={2}
                      value={perf.areasToMaintain}
                      onChange={(e) =>
                        handleSubjectPerformanceChange(
                          perf.key,
                          "areasToMaintain",
                          e.target.value,
                        )
                      }
                      placeholder="Areas to maintain..."
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Areas to Improve"
                    style={{ marginBottom: 8 }}
                  >
                    <TextArea
                      rows={2}
                      value={perf.areasToImprove}
                      onChange={(e) =>
                        handleSubjectPerformanceChange(
                          perf.key,
                          "areasToImprove",
                          e.target.value,
                        )
                      }
                      placeholder="Areas to improve..."
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Excelling Students"
                    style={{ marginBottom: 8 }}
                  >
                    <StudentMultiSelect
                      students={students || []}
                      value={perf.excellingStudents}
                      onChange={(value) =>
                        handleSubjectPerformanceChange(
                          perf.key,
                          "excellingStudents",
                          value,
                        )
                      }
                      placeholder="Select excelling students"
                      getStudentDisplay={(student) =>
                        `${student.firstName} ${student.lastName}`
                      }
                      getStudentValue={(student) => student.id}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Students Needing Help"
                    style={{ marginBottom: 8 }}
                  >
                    <StudentMultiSelect
                      students={students || []}
                      value={perf.studentsNeedingHelp}
                      onChange={(value) =>
                        handleSubjectPerformanceChange(
                          perf.key,
                          "studentsNeedingHelp",
                          value,
                        )
                      }
                      placeholder="Select students needing help"
                      getStudentDisplay={(student) =>
                        `${student.firstName} ${student.lastName}`
                      }
                      getStudentValue={(student) => student.id}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}

          {subjectPerformances.length === 0 && (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#999" }}
            >
              No Supervisor's Comments added yet. Click the button above to add
              one.
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={isEditMode ? "Edit Inspection" : "Create New Inspection"}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} size="small" style={{ marginBottom: 24 }}>
        {steps.map((step) => (
          <Step key={step.title} title={step.title} />
        ))}
      </Steps>

      <div style={{ minHeight: 300 }}>{steps[currentStep].content}</div>

      <Divider />

      <div style={{ textAlign: "right" }}>
        <Space>
          {currentStep > 0 && <Button onClick={handlePrev}>Previous</Button>}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              {isEditMode ? "Submit" : "Submit"}
            </Button>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default CreateInspectionModal;
